import { useId, useRef } from 'react';
import { ListSubheader, MenuItem } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { RwMenu } from '@/common/presentation/components/rw';
import type { LayersRowMenuItem } from './layersTypes';

export interface LayersRowMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  items: LayersRowMenuItem[];
  testId?: string;
}

const selectionRole = (item: LayersRowMenuItem): 'menuitemcheckbox' | 'menuitemradio' | undefined => {
  if (item.selected === undefined) return undefined;
  return item.selectionMode === 'radio' ? 'menuitemradio' : 'menuitemcheckbox';
};

/** The labelled item at `start` plus the items right after it that belong to its group. */
const groupMembers = (items: LayersRowMenuItem[], start: number): number[] => {
  const opener = items[start];
  if (!opener?.groupLabel || opener.selected === undefined) return start >= 0 ? [start] : [];
  const members = [start];
  for (let index = start + 1; index < items.length; index += 1) {
    const next = items[index];
    if (!next || next.groupLabel || next.selected === undefined || next.selectionMode !== opener.selectionMode) break;
    members.push(index);
  }
  return members;
};

/**
 * The row's ⋮ popup — grouped items (`groupLabel` opens a titled group above the item), a
 * trailing checkmark for the current choice (`selected`), and a destructive item styled in the
 * critical register. A destructive entry should be last in `items`; this component styles it,
 * it does not reorder it.
 *
 * A heading that opens a run of selectable items is exposed as `role="group"` named by its own
 * text and owning those items (`aria-owns`). The DOM stays flat on purpose: MUI's arrow-key
 * navigation walks sibling elements and would skip items nested inside a wrapper.
 */
export function LayersRowMenu({ anchorEl, onClose, items, testId }: LayersRowMenuProps) {
  const baseId = useId();
  // An `afterClose` action waits here for the menu's exit transition, i.e. for its focus return to be over.
  const afterClose = useRef<(() => void) | null>(null);
  const itemId = (index: number) => `${baseId}-item-${index}`;
  return (
    <RwMenu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        list: { sx: { py: 0 }, ...(testId ? { 'data-testid': testId } : {}) },
        transition: {
          onExited: () => {
            const run = afterClose.current;
            afterClose.current = null;
            run?.();
          }
        }
      }}
    >
      {items.flatMap((item, index) => {
        const owned = item.groupLabel ? groupMembers(items, index).map(itemId).join(' ') : '';
        return [
          item.groupLabel ? (
            <ListSubheader
              key={`${item.label}-heading`}
              component="div"
              role={owned ? 'group' : undefined}
              aria-labelledby={owned ? `${baseId}-label-${index}` : undefined}
              aria-owns={owned || undefined}
              sx={{ fontSize: 'var(--font-label-small-size)', lineHeight: 2, color: 'var(--text-disabled)', px: 'var(--size-12)' }}
            >
              <span id={`${baseId}-label-${index}`} aria-hidden={owned ? true : undefined}>
                {item.groupLabel}
              </span>
            </ListSubheader>
          ) : null,
          <MenuItem
            key={item.label}
            id={itemId(index)}
            disabled={item.disabled}
            selected={item.selected}
            onClick={() => {
              onClose();
              if (item.afterClose) afterClose.current = item.onClick;
              else item.onClick();
            }}
            data-testid={item.testId}
            aria-checked={item.selected === undefined ? undefined : item.selected}
            role={selectionRole(item)}
            sx={{
              gap: 'var(--size-8)',
              color: item.destructive ? 'var(--text-critical)' : undefined,
              '& .MuiSvgIcon-root': { fontSize: 'var(--size-16)' }
            }}
          >
            {item.icon}
            <span>{item.label}</span>
            {item.hint ? (
              <span style={{ fontSize: 'var(--font-label-small-size)', color: 'var(--text-disabled)' }}>{item.hint}</span>
            ) : null}
            {item.selected ? <CheckIcon sx={{ fontSize: 'var(--size-16)', ml: 'auto' }} /> : null}
          </MenuItem>
        ];
      })}
    </RwMenu>
  );
}
